'use client';

import './nav-tabs.scss';

import { useEffect, useRef } from 'react';

import { Link } from '../../atoms/link/link';

/**
 * Ensures that active tab is fully visibile and flush left on mobile.
 * For now we're just applying this for all screen sizes if focusOnMobile is true.
 */
const anchorActiveTab = (container: HTMLDivElement, list: HTMLDivElement) => {
  if (!container || !list) {
    return;
  }

  // Find the active tab (this is fragile)
  const activeTab = container.querySelector<HTMLElement>(
    '[aria-current="true"]',
  );
  if (!activeTab) {
    return;
  }

  const containerRect = container.getBoundingClientRect();
  const tabRect = activeTab.getBoundingClientRect();
  const isFullyVisible = tabRect.right <= window.innerWidth;

  if (isFullyVisible) {
    return;
  }

  const containerWidth = containerRect.width;

  // First we may need to add some whitespace to container so it always enough room on the right to scroll entire way.
  const paddingNeeded = Math.max(0, containerWidth - activeTab.offsetWidth);
  if (paddingNeeded > 0) {
    // eslint-disable-next-line no-param-reassign
    list.style.paddingRight = `${paddingNeeded}px`;
  }

  // Scroll horizontally so the active tab is flush left
  const targetLeft = tabRect.left - containerRect.left + container.scrollLeft;
  // eslint-disable-next-line no-param-reassign
  container.scrollLeft = targetLeft;
};

export type LinkPlaceholder = {
  id: string;
  title: string;
  path: string;
  /**
   * In almost all cases this should be automatically handled by the Link component.
   */
  isActive?: boolean;
};

export type NavTabsProps = {
  links: LinkPlaceholder[];
  focusOnMobile?: boolean;
};

// TODO: accessibility considerations we need to have in mind for these tertiary navs
export const NavTabs: React.FC<NavTabsProps> = ({
  links,
  focusOnMobile = true,
}) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!focusOnMobile) {
      return;
    }

    const container = scrollContainerRef.current;
    const list = listRef.current;
    if (!container || !list) {
      return;
    }

    anchorActiveTab(container, list);
  }, []);

  if (!links || links.length === 0) {
    return null;
  }

  return (
    <nav className="NavTabs relative w-100 overflow-hidden mb-comfy-xl">
      <div
        ref={scrollContainerRef}
        className="NavTabs__scrollcontainer overflow-x-auto"
      >
        <div
          ref={listRef}
          className="NavTabs__list flex gap-comfy-md"
          role="tablist"
        >
          {links.map((link) => {
            return (
              <Link
                key={link.id}
                href={link.path}
                className="NavTabs__tab label-potent label-lg pr-snug-md pb-comfy-xs pl-snug-md flex-shrink-0"
                // This is a very rare override.
                // In almost all cases this should be automatically handled by the Link component.
                {...(link.isActive && { 'aria-current': 'true' })}
              >
                {link.title}
              </Link>
            );
          })}
        </div>
      </div>
    </nav>
  );
};
